import React, { useState, useEffect, useCallback, memo } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Users,
  BookOpen,
  Video,
  FileText,
  ClipboardList,
  ClipboardCheck,
  Bookmark,
  FileArchive,
  UserPlus,
  LogOut,
  Bell,
  Settings,
  GraduationCap,
  Calendar,
  Sparkles
} from "lucide-react";
import { GiTeacher } from "react-icons/gi";
import { FaWhatsapp, FaBell } from "react-icons/fa";
import PropTypes from "prop-types";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { query, ref, onValue, orderByChild, equalTo, get, update } from "firebase/database";
import logo from "./assets/x_math_logo.png";

const AdminLayout = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [adminName, setAdminName] = useState("Admin");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAdminName("Admin");
        return;
      }

      let resolvedName = user.displayName || "";
      try {
        if (!resolvedName) {
          const usersRef = ref(db, "users");
          const snapshot = await get(usersRef);
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            const foundUser = Object.values(usersData).find((u) => u.email === user.email);
            if (foundUser) {
              resolvedName = foundUser.name || foundUser.fullName || foundUser.username || foundUser.displayName || "";
            }
          }
        }
      } catch (e) {
        // ignore and fallback
      }

      if (!resolvedName && user.email) {
        resolvedName = user.email.split("@")[0];
      }

      setAdminName(resolvedName || "Admin");
    });

    return () => unsubscribe();
  }, []);

  const navigationItems = [
    {
      category: "Main",
      items: [
        { name: "Dashboard", path: "/admin/dashboard", icon: <LayoutDashboard size={18} /> },
        { name: "Courses", path: "/admin/courses", icon: <Settings size={18} /> },
        { name: "Users", path: "/admin/users", icon: <Users size={18} /> },
        // { name: "Students", path: "/admin/students", icon: <Users size={20} /> },
      ]
    },
    {
      category: "Content",
      items: [
        { name: "Chapters", path: "/admin/chapters", icon: <BookOpen size={18} /> },
        { name: "Videos", path: "/admin/videos", icon: <Video size={18} /> },
        { name: "Mark Schemes", path: "/admin/markschemes", icon: <FileText size={18} /> },
        { name: "Extra Questions", path: "/admin/extra-questions", icon: <FileText size={18} /> },
        { name: "PDFs", path: "/admin/pdfs", icon: <FileText size={18} /> },
        { name: "Past Papers", path: "/admin/pastpapers", icon: <FileArchive size={18} /> },
      ]
    },
    {
      category: "Academic",
      items: [
        { name: "Homework Ann.", path: "/admin/homeworks", icon: <ClipboardList size={18} /> },
        { name: "Homework Tracking", path: "/admin/hwtracking", icon: <ClipboardCheck size={18} /> },
        { name: "Exams", path: "/admin/exams", icon: <Bookmark size={18} /> },
        { name: "Attendance", path: "/admin/attendance", icon: <Calendar size={18} /> },
      ]
    },
    {
      category: "Communication",
      items: [
        { name: "Online Meetings", path: "/admin/online-meeting", icon: <Calendar size={18} /> },
        { name: "Registration Requests", path: "/admin/registration-requests", icon: <UserPlus size={18} /> },
        { name: "Announcements", path: "/admin/announcements", icon: <Bell size={18} /> },
        { name: "Holiday Greeting", path: "/admin/holiday-greeting", icon: <Sparkles size={18} /> },
      ]
    }
  ];

  return (
    <div className="flex bg-slate-50 text-gray-900 min-h-screen">
      {/* Sidebar Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 sm:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full bg-white transition-all duration-300 ease-in-out z-50 flex flex-col border-r border-slate-200 ${
          sidebarOpen ? "translate-x-0 w-72" : "-translate-x-full w-72"
        } sm:translate-x-0 ${sidebarCollapsed ? "sm:w-[4.5rem]" : "sm:w-72"} sm:shadow-lg`}
      >
        {/* Header */}
        <div className="flex items-center justify-between shrink-0 h-16 px-4 sm:px-5 border-b border-slate-200/80 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800">
          {!sidebarCollapsed ? (
            <div className="flex-1 flex justify-center min-w-0">
              <Link to="/admin/dashboard" className="flex items-center">
                <img src={logo} alt="X-Maths logo" className="h-11 w-auto max-w-[180px] object-contain brightness-0 invert" />
              </Link>
            </div>
          ) : (
            <Link to="/admin/dashboard" className="flex items-center justify-center w-full py-2">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center overflow-hidden">
                <img src={logo} alt="X-Maths" className="h-8 w-8 object-contain brightness-0 invert" />
              </div>
            </Link>
          )}
          <button
            className="sm:hidden p-2 -mr-1 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-5 px-3 min-h-0">
          <div className="space-y-6">
            {navigationItems.map((section) => (
              <div key={section.category}>
                {!sidebarCollapsed && (
                  <p className="px-4 mb-3 text-[11px] font-semibold text-slate-500 uppercase tracking-widest">
                    {section.category}
                  </p>
                )}
                {sidebarCollapsed && <div className="h-1" />}
                <ul className="space-y-0.5">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.path}
                          title={sidebarCollapsed ? item.name : undefined}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center rounded-lg transition-all duration-200 ${
                            sidebarCollapsed ? "justify-center p-3 mx-1.5" : "gap-3 px-4 py-2.5 mx-1.5"
                          } ${
                            isActive
                              ? "bg-blue-600 text-white shadow-sm"
                              : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                          }`}
                        >
                          <span className={`shrink-0 ${isActive ? "text-white" : "text-slate-500"}`}>
                            {item.icon}
                          </span>
                          {!sidebarCollapsed && (
                            <span className="text-sm font-medium truncate">{item.name}</span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 bg-slate-50/80 space-y-2 shrink-0">
          <button
            type="button"
            onClick={() => setSidebarCollapsed((c) => !c)}
            className="hidden sm:flex items-center justify-center w-full gap-2 py-3 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200/80 transition-colors"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight size={22} strokeWidth={2} /> : <><ChevronLeft size={22} strokeWidth={2} /><span className="text-sm font-medium">Collapse</span></>}
          </button>
          <Link
            to="/logout"
            className={`flex items-center rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors ${
              sidebarCollapsed ? "justify-center p-3" : "gap-3 px-4 py-3"
            }`}
            onClick={() => setSidebarOpen(false)}
            title={sidebarCollapsed ? "Logout" : undefined}
          >
            <LogOut size={20} className="shrink-0" strokeWidth={1.8} />
            {!sidebarCollapsed && <span className="text-sm font-medium">Logout</span>}
          </Link>
        </div>
      </div>

      <div className={`flex flex-col w-full transition-[margin] duration-300 ease-in-out ${sidebarCollapsed ? "sm:ml-[4.5rem]" : "sm:ml-72"}`}>
        {/* Top bar */}
        <header className={`fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-sm border-b border-slate-200 flex justify-between items-center z-40 transition-[padding] duration-300 ease-in-out px-5 sm:px-8 ${sidebarCollapsed ? "sm:pl-[4.5rem]" : "sm:pl-72"}`}>
          <div className="flex items-center gap-3">
            <button
              className="sm:hidden p-2 -ml-1 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
            <Link to="/admin/dashboard" className="sm:hidden flex items-center">
              <img src={logo} alt="X-Maths" className="h-9 w-auto" />
            </Link>
            <div className="hidden sm:block ml-1">
              <h1 className="text-lg font-semibold text-slate-900">Hello, {adminName}</h1>
              <p className="text-xs text-slate-500 mt-0.5">Have a great day</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 pl-4 ml-2 border-l border-slate-200">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                {adminName?.[0]?.toUpperCase() || "A"}
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-slate-900">{adminName}</p>
                <p className="text-xs text-slate-500">Administrator</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-5 sm:p-8 pt-4 sm:pt-6 bg-slate-50/80 min-h-screen mt-16">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
